import React, {useEffect, useState} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'
import * as auth from './redux/AuthRedux'
import {RootState} from '../../../setup'
import {GetAPIDomain, GetAPIHeaders} from '../../infrastracture/Helper'
import {clearPkceSession} from './pkce/pkce'

export function Logout() {
  const dispatch = useDispatch()
  const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as any
  const {pathname} = useLocation()

  // A project guest signing out belongs back on the portal login, not the CRM one. Captured on
  // first render, because the async block below dispatches logout() and wipes localStorage —
  // every source is gone by the time it finishes.
  const [loginPath] = useState(() => {
    // The route is the only signal that cannot erode, so the guest portal signs out through
    // /projects/guest/logout and is answered from that alone. The two checks below stay as the
    // answer for /logout, but neither is dependable on its own: redux's user is undefined for
    // the whole window between UserRequested and UserLoaded, and the localStorage marker —
    // written only by ProjectGuestLogin, never restored — is destroyed by any
    // localStorage.clear(), including the one the axios refresh interceptor runs on every 401
    // a cookie-authenticated guest receives.
    if (pathname.startsWith('/projects/guest')) return '/projects/guest/login'

    return user?.isProjectGuest || localStorage.getItem('is-project-guest') === 'true'
      ? '/projects/guest/login'
      : '/login'
  })

  useEffect(() => {
    const signOut = async () => {
      try {
        const authDomain = await GetAPIDomain()

        await fetch(authDomain + '/connect/log-out', {
          body: JSON.stringify({}),
          method: 'POST',
          headers: GetAPIHeaders('application/json'),
          credentials: 'include',
        })
      } catch {
        // Server-side sign-out is best-effort — always tear the client session down anyway.
      }

      dispatch(auth.actions.logout())
      clearPkceSession()
      localStorage.clear()

      // Hard navigation *after* the session is gone, rather than rendering a <Redirect> up
      // front. A <Redirect> fires while the guest is still authenticated, and Routes.tsx
      // deliberately won't render /projects/guest/login for an authenticated guest — it would
      // match the non-exact /projects/guest fork instead, bounce to the portal home, and then
      // land on /login once logout() propagated. A full page load avoids that race entirely.
      document.location.href = loginPath
    }

    signOut()
  }, [dispatch, loginPath])

  return (
    <div className='d-flex flex-center' style={{minHeight: '100vh'}}>
      <span className='text-muted'>Signing out...</span>
    </div>
  )
}